"use client";

import { useQueryClient } from "@tanstack/react-query";

import { useCallback, useState } from "react";

import type { PreloadedFaxAttachment } from "@/components/fax/compose-fax-form";
import type { NoteExportDestination } from "@/components/scribe/print-note-dialog";

import { useTenant } from "@/contexts/tenant-provider";

import { useClinicClock } from "@/hooks/use-clinic-clock";
import { fetchIcd10Descriptions } from "@/hooks/use-icd10-descriptions";

import type { Icd10DescriptionMap } from "@/lib/billing/icd10-descriptions";
import type { ClinicClock } from "@/lib/clinic-clock";
import { formatStaffName } from "@/lib/format-data";
import { isIcd10CodesQuestion, readIcd10Answer } from "@/lib/icd10-codes-field";
import { resolveLogoDataUrl } from "@/lib/logo-data-url";
import {
  buildNotePhotosHtml,
  getPrintablePhotos,
} from "@/lib/note-print-photos";
import {
  appendSignOffSignatures,
  escapeNoteHtml,
  formatNoteAnswersToText,
  mergeAnswersWithLocalChanges,
  stripHtmlToText,
} from "@/lib/note-utils";
import { parseError } from "@/lib/parse-error";
import { renderPrintHtmlToPdf } from "@/lib/print-html-to-pdf";
import { usePrintNote } from "@/lib/print-utils";
import { nameShowsCredentials } from "@/lib/staff-name-display";
import { useToast } from "@/lib/use-toast";

import {
  NoteAttachment,
  NoteDetail,
  NoteListItem,
  notePatientToPatientSummary,
} from "@/types/notes";

import { providerQueryOptions } from "@/api/provider/admin/provider-query";
import { uploadFaxAttachment } from "@/api/provider/fax/faxes";
import { noteQueryOptions } from "@/api/provider/scribe/note-query";

/** The note, rendered and uploaded, ready to hand to the fax composer. */
export interface NoteFaxHandoff {
  patientId: string;
  attachment: PreloadedFaxAttachment;
}

export interface UseNotePrintFlowResult {
  state: {
    isDialogOpen: boolean;
    isPreparing: boolean;
    isPrinting: boolean;
    /** True while the note is being rendered to PDF and uploaded for faxing. */
    isFaxing: boolean;
    noteDetailForDialog: NoteDetail | null;
    /** Number of photos attached to the note being printed. */
    photoCount: number;
    /**
     * Set once a note has been rendered and uploaded; the host opens the fax
     * composer on it. Null whenever no fax is in flight.
     */
    faxHandoff: NoteFaxHandoff | null;
  };
  actions: {
    requestPrint: (
      note: NoteListItem | NoteDetail,
      // Unsaved draft answer edits, so the print dialog and export reflect the
      // same conditional visibility as the on-screen editor rather than just
      // the last saved answers. Optional — omit when the caller has no draft
      // state (e.g. the notes list, or read-only note views).
      localAnswerChanges?: Record<string, Record<string, any>>,
    ) => Promise<void>;
    confirmPrint: (options: {
      excludedQuestionIds: Set<string>;
      hideMedications: boolean;
      hideMedicationDosage: boolean;
      hideMedicalInformation: boolean;
      includePhotos: boolean;
      /** Where the document goes. Defaults to the printer. */
      destination?: NoteExportDestination;
    }) => Promise<void>;
    closeDialog: () => void;
    /** Dismiss the fax composer, discarding the uploaded copy of the note. */
    closeFaxDialog: () => void;
  };
}

const isDetailedNote = (note: NoteListItem | NoteDetail): note is NoteDetail =>
  "template" in note && "patient" in note;

export const toNoteListItemForPrint = (
  note: NoteListItem | NoteDetail,
): NoteListItem => {
  if (!isDetailedNote(note)) return note;

  return {
    id: note.id,
    eventId: note.eventId,
    summary: note.summary,
    title: note.title,
    templateId: note.template?.id,
    createdDate: note.createdDate,
    visitDate: note.visitDate ?? note.createdDate,
    lastEditedDate: note.createdDate,
    patient: notePatientToPatientSummary(note.patient),
    providers: note.providers,
    // Carries the note's location through to the printed header's location of
    // service; without it the header field has nothing to resolve.
    locationId: note.locationId,
    version: note.version,
    aiGenerated: note.aiGenerated,
    sourceDocuments: note.sourceDocuments,
    signedOffProviderId: note.signedOffProviderId,
    signedOffDate: note.signedOffDate,
    hasAnswers: (note.answers?.length ?? 0) > 0,
    answers: note.answers,
    icd10Codes: note.icd10Codes,
    cptCodes: note.cptCodes,
    cptModifiers: note.cptModifiers,
    expiryDate: note.expiryDate,
    isPinned: note.isPinned,
  };
};

const icd10CodesOnNote = (noteDetail: NoteDetail): string[] => {
  const questionIds = new Set(
    (noteDetail.template?.questions ?? [])
      .filter((question) => isIcd10CodesQuestion(question))
      .map((question) => question.id),
  );
  return (noteDetail.answers ?? [])
    .filter((answer) => questionIds.has(answer.questionId))
    .flatMap((answer) => readIcd10Answer(answer.data).codes);
};

// The summary is a raw narrative of the visit: it is not condition-filtered and
// it honors none of the dialog's exclusions. It may therefore only stand in when
// the note has no structured content at all, which is the one case
// formatNoteAnswersToText reports as `undefined` — content that exists but was
// filtered away comes back as an empty string and must stay empty.
//
// The printed document is assembled as HTML, so the summary is escaped rather
// than concatenated in raw.
const getFullNoteContent = (noteDetail: NoteDetail): string =>
  formatNoteAnswersToText(noteDetail, undefined, { outputHtml: true }) ??
  (noteDetail.summary
    ? `<p>${escapeNoteHtml(stripHtmlToText(noteDetail.summary))}</p>`
    : "");

/**
 * Name the faxed copy the way a clinic files paper: patient surname first, then
 * what the document is, then the date of service. It is what the recipient sees
 * in the composer's attachment list.
 */
const buildFaxFilename = (
  noteDetail: NoteDetail,
  clock: ClinicClock,
): string => {
  const visit = noteDetail.visitDate ?? noteDetail.createdDate;
  // The clinic's day, as on the printed header; a date-only visit passes through.
  const datePart =
    visit && !Number.isNaN(new Date(visit).getTime()) ? clock.dayOf(visit) : "";

  const label = [
    [noteDetail.patient?.lastName, noteDetail.patient?.firstName]
      .filter(Boolean)
      .join(" "),
    noteDetail.template?.name || noteDetail.title || "Note",
    datePart,
  ]
    .filter(Boolean)
    .join(" - ")
    // GCS keys off the extension only, but the filename rides through a
    // multipart upload and back out to the fax list — keep it boring.
    .replace(/[^\w\-. ]+/g, "")
    .trim();

  return `${label || "Note"}.pdf`;
};

export function useNotePrintFlow(): UseNotePrintFlowResult {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { tenant } = useTenant();
  const clock = useClinicClock();
  const { printNote, buildNoteDocumentHtml } = usePrintNote();

  const [pendingNoteItem, setPendingNoteItem] = useState<NoteListItem | null>(
    null,
  );
  const [noteDetailForDialog, setNoteDetailForDialog] =
    useState<NoteDetail | null>(null);
  const [photoAttachments, setPhotoAttachments] = useState<NoteAttachment[]>(
    [],
  );
  const [icd10Descriptions, setIcd10Descriptions] =
    useState<Icd10DescriptionMap>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isFaxing, setIsFaxing] = useState(false);
  const [faxHandoff, setFaxHandoff] = useState<NoteFaxHandoff | null>(null);

  const closeDialog = useCallback(() => {
    // Closing mid-export would strand the print window or the upload.
    if (isPrinting || isFaxing) return;
    setIsDialogOpen(false);
    setNoteDetailForDialog(null);
    setPendingNoteItem(null);
    setPhotoAttachments([]);
  }, [isPrinting, isFaxing]);

  const closeFaxDialog = useCallback(() => setFaxHandoff(null), []);

  const fetchNoteDetail = useCallback(
    async (
      note: NoteListItem | NoteDetail,
    ): Promise<NoteDetail | undefined> => {
      if (isDetailedNote(note) && note.template && note.answers) return note;
      return queryClient.fetchQuery(noteQueryOptions.detail(note.id));
    },
    [queryClient],
  );

  const requestPrint = useCallback(
    async (
      note: NoteListItem | NoteDetail,
      localAnswerChanges?: Record<string, Record<string, any>>,
    ) => {
      setIsPreparing(true);

      try {
        // The note's photos never appear as answers, so they're fetched
        // alongside the detail. A failed fetch only costs the photo option —
        // it must not block the printout.
        const [noteDetail, attachments] = await Promise.all([
          fetchNoteDetail(note),
          queryClient
            .fetchQuery(noteQueryOptions.attachments(note.id))
            .catch(() => null),
        ]);
        if (!noteDetail) {
          toast({
            title: "Error",
            description: "Unable to fetch note details for printing",
            variant: "error",
          });
          return;
        }

        // Bake any unsaved draft edits into the answers up front, so the
        // "select what to include" dialog (which reads noteDetail.answers
        // directly) and the eventual export both agree with what the editor
        // currently shows instead of silently falling back to the last saved
        // answers.
        const noteDetailWithDraft: NoteDetail =
          localAnswerChanges && Object.keys(localAnswerChanges).length > 0
            ? {
                ...noteDetail,
                answers: mergeAnswersWithLocalChanges(
                  noteDetail,
                  localAnswerChanges,
                ),
              }
            : noteDetail;

        // Looked up now, while the dialog is still on its way: the export runs
        // on the dialog's click, and `window.open` only succeeds while that
        // click's user activation is live, so it cannot wait on a fetch.
        setIcd10Descriptions(
          await fetchIcd10Descriptions(icd10CodesOnNote(noteDetailWithDraft)),
        );

        // Always surface the export dialog so the provider can choose which
        // parts to include and toggle medication-dosage visibility. Medical
        // codes, medications, and demographics print even when the note itself
        // has no selectable fields.
        setPendingNoteItem(toNoteListItemForPrint(noteDetailWithDraft));
        setNoteDetailForDialog(noteDetailWithDraft);
        setPhotoAttachments(getPrintablePhotos(attachments));
        setIsDialogOpen(true);
      } catch (error) {
        const errorMessage = await parseError(error);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "error",
        });
      } finally {
        setIsPreparing(false);
      }
    },
    [fetchNoteDetail, queryClient, toast],
  );

  /**
   * Everything the export needs that doesn't depend on where it is headed: the
   * note content filtered down to the dialog's selection, plus the signing
   * provider's signature block.
   */
  const buildDocumentContent = useCallback(
    async (
      noteDetail: NoteDetail,
      excludedQuestionIds: Set<string>,
    ): Promise<{ filteredContent: string; signatureContent: string }> => {
      // Only reaches getFullNoteContent for a note with nothing structured to
      // print. Excluding every part (or gating every question) yields an empty
      // string, not `undefined`, so the printout stays empty rather than
      // reverting to the unfiltered note.
      const filteredContent =
        formatNoteAnswersToText(noteDetail, excludedQuestionIds, {
          // A printed note is a clinical document: each question reads as a
          // subheading with its answer as prose beneath, not "Label: value".
          proseLayout: true,
          // Emitted as sanitized HTML so the answer's own formatting survives.
          outputHtml: true,
          icd10Descriptions,
        }) ?? getFullNoteContent(noteDetail);

      // The signing provider's signature goes to the bottom of the export,
      // after the photos — hence kept separate from the note content.
      let signatureContent = "";
      if (noteDetail.signedOffProviderId) {
        try {
          const providers = await queryClient.fetchQuery(
            providerQueryOptions.list(),
          );
          const signer = providers?.find(
            (p) => p.id === noteDetail.signedOffProviderId,
          );
          if (signer) {
            signatureContent = appendSignOffSignatures(
              "",
              [
                {
                  name: formatStaffName(signer),
                  // Already in the name when the signer chose to show them there.
                  credentials: nameShowsCredentials(signer)
                    ? undefined
                    : signer.credentials,
                  signatureImageUrl: signer.signatureImageUrl,
                  signedDate: noteDetail.signedOffDate,
                },
              ],
              { outputHtml: true },
            );
          }
        } catch {
          // Non-fatal: export without the appended signature block.
        }
      }

      return { filteredContent, signatureContent };
    },
    [icd10Descriptions, queryClient],
  );

  // Per-photo failures degrade to a placeholder rather than aborting the
  // export, so the count is the only way the provider learns that an audit copy
  // is short of evidence.
  const warnAboutFailedPhotos = useCallback(
    (failedPhotos: number) => {
      if (failedPhotos <= 0) return;
      toast({
        title:
          failedPhotos === 1
            ? "1 photo could not be included"
            : `${failedPhotos} photos could not be included`,
        description:
          "They appear as placeholders in the document. Check the note's photos before using this copy for an audit.",
        variant: "error",
      });
    },
    [toast],
  );

  const clearDialogState = useCallback(() => {
    setIsDialogOpen(false);
    setNoteDetailForDialog(null);
    setPendingNoteItem(null);
    setPhotoAttachments([]);
  }, []);

  const printDocument = useCallback(
    async ({
      noteItem,
      noteDetail,
      filteredContent,
      signatureContent,
      hideMedications,
      hideMedicationDosage,
      hideMedicalInformation,
      includePhotos,
    }: {
      noteItem: NoteListItem;
      noteDetail: NoteDetail;
      filteredContent: string;
      signatureContent: string;
      hideMedications: boolean;
      hideMedicationDosage: boolean;
      hideMedicalInformation: boolean;
      includePhotos: boolean;
    }) => {
      setIsPrinting(true);
      try {
        // Passed as a thunk, not an awaited string: `printMarkdown` runs it
        // after opening the print window, because `window.open` only succeeds
        // while the click's transient user activation is live and fetching a
        // dozen photos outlasts it.
        let failedPhotos = 0;
        const buildPhotos = async () => {
          const { html, failed } = await buildNotePhotosHtml(photoAttachments);
          failedPhotos = failed;
          return html;
        };

        const result = await printNote(noteItem, filteredContent, {
          hideMedications,
          hideMedicationDosage,
          hideMedicalInformation,
          photosHtml:
            includePhotos && photoAttachments.length > 0 ? buildPhotos : "",
          signatureContent,
          encounterType: noteDetail.template?.name,
        });

        warnAboutFailedPhotos(failedPhotos);
        if (result.success) clearDialogState();
      } finally {
        setIsPrinting(false);
      }
    },
    [clearDialogState, photoAttachments, printNote, warnAboutFailedPhotos],
  );

  const faxDocument = useCallback(
    async ({
      noteItem,
      noteDetail,
      filteredContent,
      signatureContent,
      hideMedications,
      hideMedicationDosage,
      hideMedicalInformation,
      includePhotos,
    }: {
      noteItem: NoteListItem;
      noteDetail: NoteDetail;
      filteredContent: string;
      signatureContent: string;
      hideMedications: boolean;
      hideMedicationDosage: boolean;
      hideMedicalInformation: boolean;
      includePhotos: boolean;
    }) => {
      setIsFaxing(true);
      try {
        // Nothing here races a popup, so the photos and the logo are resolved
        // up front: the document is rasterized in-page, and an image that
        // arrives late (or cross-origin) is an image missing from the fax.
        let failedPhotos = 0;
        let photosHtml = "";
        if (includePhotos && photoAttachments.length > 0) {
          const built = await buildNotePhotosHtml(photoAttachments);
          photosHtml = built.html;
          failedPhotos = built.failed;
        }

        const built = await buildNoteDocumentHtml(noteItem, filteredContent, {
          hideMedications,
          hideMedicationDosage,
          hideMedicalInformation,
          photosHtml,
          signatureContent,
          encounterType: noteDetail.template?.name,
          logoDataUrl: await resolveLogoDataUrl(tenant?.logoUrl),
        });
        if (!built.success) throw new Error(built.error);

        const { blob } = await renderPrintHtmlToPdf(built.html);
        const filename = buildFaxFilename(noteDetail, clock);
        const uploaded = await uploadFaxAttachment(
          new File([blob], filename, { type: "application/pdf" }),
        );
        if (!uploaded) throw new Error("Could not upload the note to fax.");

        warnAboutFailedPhotos(failedPhotos);
        setFaxHandoff({
          patientId: noteDetail.patient.id,
          attachment: {
            gcsPath: uploaded.gcsPath,
            filename: uploaded.filename || filename,
            // Not a chart document: this is a copy rendered for this fax, the
            // same as a PDF the user dropped into the composer themselves.
            sourceType: "UPLOAD",
          },
        });
        clearDialogState();
      } catch (error) {
        toast({
          title: "Couldn't prepare the fax",
          description:
            error instanceof Error
              ? error.message
              : "Unable to prepare this note for faxing.",
          variant: "error",
        });
      } finally {
        setIsFaxing(false);
      }
    },
    [
      buildNoteDocumentHtml,
      clearDialogState,
      clock,
      photoAttachments,
      tenant?.logoUrl,
      toast,
      warnAboutFailedPhotos,
    ],
  );

  const confirmPrint = useCallback(
    async ({
      excludedQuestionIds,
      hideMedications,
      hideMedicationDosage,
      hideMedicalInformation,
      includePhotos,
      destination = "print",
    }: {
      excludedQuestionIds: Set<string>;
      hideMedications: boolean;
      hideMedicationDosage: boolean;
      hideMedicalInformation: boolean;
      includePhotos: boolean;
      destination?: NoteExportDestination;
    }) => {
      if (!noteDetailForDialog) return;
      const noteItem =
        pendingNoteItem ?? toNoteListItemForPrint(noteDetailForDialog);

      const { filteredContent, signatureContent } = await buildDocumentContent(
        noteDetailForDialog,
        excludedQuestionIds,
      );

      const args = {
        noteItem,
        noteDetail: noteDetailForDialog,
        filteredContent,
        signatureContent,
        hideMedications,
        hideMedicationDosage,
        hideMedicalInformation,
        includePhotos,
      };

      if (destination === "fax") await faxDocument(args);
      else await printDocument(args);
    },
    [
      buildDocumentContent,
      faxDocument,
      noteDetailForDialog,
      pendingNoteItem,
      printDocument,
    ],
  );

  return {
    state: {
      isDialogOpen,
      isPreparing,
      isPrinting,
      isFaxing,
      noteDetailForDialog,
      photoCount: photoAttachments.length,
      faxHandoff,
    },
    actions: {
      requestPrint,
      confirmPrint,
      closeDialog,
      closeFaxDialog,
    },
  };
}
